import * as signalR from '@microsoft/signalr'
import { action, autorun, makeObservable, observable, runInAction } from 'mobx'
import { Event, IMessageBus } from '../lib'
import { authStore } from './AuthStore'

export interface IHubConnection {
  signalRType: string
  hubType: string
}

enum ConnectionState {
  Offline = 'offline',
  Connecting = 'connecting',
  Connected = 'connected',
}

const connectionBuilder = (
  onReconnected: () => void,
  onClose: (error?: Error) => void,
  url: string,
  useLongPolling = false,
) => {
  const connection = new signalR.HubConnectionBuilder()
    .withUrl(url, {
      withCredentials: false,
      transport: useLongPolling
        ? signalR.HttpTransportType.LongPolling
        : signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
    })
    .withAutomaticReconnect([
      1000, 2000, 2000, 3000, 3000, 5000, 5000, 10000, 20000, 20000, 30000, 30000,
    ]) //TOLATER: maybe find a better policy
    .configureLogging(signalR.LogLevel.Debug)
    .build()
  connection.onreconnected(onReconnected)
  connection.onclose(onClose)
  return connection
}

export class HubContext {
  protected connection: signalR.HubConnection
  connectionState = ConnectionState.Offline
  joinedGroup = false
  isLoggingIn = false
  retries = 0
  private isStopping = false
  private registeredHubConnections: IHubConnection[] = []

  constructor(
    private url: string,
    private messageBus?: IMessageBus,
  ) {
    this.connection = connectionBuilder(this.onReconnected, this.onClose, this.url)

    makeObservable(this, {
      connectionState: observable,
      loggedIn: action,
    })

    if (authStore.isLoaded) {
      this.loggedIn(true)
    } else {
      autorun(() => {
        this.loggedIn(authStore.isLoaded)
      })
    }
  }

  private onReconnected = async () => {
    if (this.isLoggingIn) {
      await this.authenticate()
    }
  }

  private onClose = (error?: Error) => {
    runInAction(() => {
      this.connectionState = ConnectionState.Offline
    })
    if (this.isStopping) {
      this.isStopping = false
      return
    }
    if (error) {
      console.error('Connection closed with error:', error)
    } else {
      console.warn('Connection closed without error, attempting restart')
    }
    this.restartConnection()
  }

  private restartConnection = async () => {
    this.retries++
    this.connection = connectionBuilder(this.onReconnected, this.onClose, this.url, true)
    this.reregisterHubConnections()
    await this.startConnection()
  }

  loggedIn = (loggedIn: boolean) => {
    if (!this.isLoggingIn && loggedIn) {
      this.isLoggingIn = true
      if (this.connectionState === ConnectionState.Connected) {
        this.authenticate()
      }
    }
  }

  startConnection = async () => {
    if (this.connectionState === ConnectionState.Offline) {
      runInAction(() => {
        this.connectionState = ConnectionState.Connecting
      })
      try {
        await this.connection.start()
        this.retries = 0
        runInAction(() => {
          this.connectionState = ConnectionState.Connected
        })
        if (this.isLoggingIn) {
          await this.authenticate()
        }
      } catch (error) {
        runInAction(() => {
          this.connectionState = ConnectionState.Offline
        })
        console.error('Error starting connection:', error)
        await this.restartConnection()
      }
    }
  }

  stopConnection = async () => {
    if (this.connectionState === ConnectionState.Connected) {
      try {
        this.isStopping = true
        await this.connection.stop()
        runInAction(() => {
          this.connectionState = ConnectionState.Offline
        })
      } catch (error) {
        this.isStopping = false
        console.error('Error stopping connection:', error)
      }
    }
  }

  registerPassThroughConnection(type: string) {
    return this.registerHubConnection({
      signalRType: type,
      hubType: type,
    })
  }

  registerHubConnection(hubConnection: IHubConnection) {
    this.registeredHubConnections.push(hubConnection)
    this.bindHubConnection(hubConnection)
  }

  private bindHubConnection(hubConnection: IHubConnection) {
    this.connection.on(hubConnection.signalRType, (data: any) => {
      console.log('backend published event ' + hubConnection.hubType)
      const messageRes = new Event(hubConnection.hubType, data)
      if (this.messageBus) {
        this.messageBus.dispatchEvent(messageRes)
      }
    })
  }

  private reregisterHubConnections = () => {
    for (const hubConnection of this.registeredHubConnections) {
      this.bindHubConnection(hubConnection)
    }
  }

  authenticate = async () => {
    try {
      await this.connection.send('Authenticate', authStore.token)
      console.log('HubContext authenticates user')
    } catch (error) {
      console.log(error)
    }
  }
}
